import { useCallback, useEffect, useState } from "react";
import {
  type DlqEntryDetail,
  type DlqEntrySummary,
  fetchDlqDetail,
  replayDlqEntry,
} from "../../api/admin-dlq";
import {
  ErrorBanner,
  LoadingIndicator,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import { formatBytes, formatDlqDate } from "./dlq-formatters";
import { styles } from "./dlq-styles";

export function DlqDetailPanel({
  entry,
  onReplayDone,
}: {
  entry: DlqEntrySummary;
  onReplayDone: () => void;
}): React.JSX.Element {
  const { detail, error, loading, replaying, replay } = useDlqDetail(entry);
  return (
    <div style={ts.detailPanel}>
      {error && <ErrorBanner message={error} />}
      {loading && <LoadingIndicator />}
      {detail && (
        <>
          <DetailGrid detail={detail} />
          <pre style={styles.bodyPreview}>{detail.body}</pre>
          <div style={styles.actions}>
            <button
              type="button"
              style={{ ...ts.actionBtn, ...ts.dangerBtn }}
              disabled={replaying}
              onClick={() => void replay(onReplayDone)}
            >
              {replaying ? t("dlq.replaying") : t("dlq.replay")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function useDlqDetail(entry: DlqEntrySummary): {
  readonly detail: DlqEntryDetail | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly replaying: boolean;
  readonly replay: (onDone: () => void) => Promise<void>;
} {
  const [detail, setDetail] = useState<DlqEntryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [replaying, setReplaying] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await fetchDlqDetail(entry));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [entry]);

  async function replay(onDone: () => void): Promise<void> {
    setReplaying(true);
    setError(null);
    try {
      await replayDlqEntry(entry);
      await load();
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.action_failed"));
    } finally {
      setReplaying(false);
    }
  }

  useEffect(() => {
    void load();
  }, [load]);

  return { detail, error, loading, replaying, replay };
}

function DetailGrid({ detail }: { detail: DlqEntryDetail }): React.JSX.Element {
  const rows = [
    [t("dlq.col_queue"), detail.origin_queue],
    [t("dlq.col_partition"), detail.partition_date],
    [t("dlq.col_attempts"), String(detail.attempts)],
    [t("dlq.body_bytes"), formatBytes(detail.body_bytes)],
    [t("dlq.uploaded_at"), formatDlqDate(detail.uploaded_at)],
    [t("dlq.replayed_at"), formatDlqDate(detail.replayed_at)],
  ];
  return (
    <div style={styles.detailGrid}>
      {rows.map(([label, value]) => (
        <div key={label}>
          <div style={styles.label}>{label}</div>
          <div style={styles.mono}>{value}</div>
        </div>
      ))}
    </div>
  );
}
